// Shared scheduling rules for the interactive prototype.
// A booking occupies every explicit resource plus the room required by its service.

import { ROOM_DUTIES } from "@/data/seed";
import { formatDateValue, getRuntimeLanguage } from "@/i18n/core";
import { localizeCatalogSource, translateKey } from "@/i18n/registry";

const ACTIONABLE_SCHEDULE_STATES = new Set(["hold", "pending", "confirmed"]);

export function isActionableScheduleBooking(booking) {
  return ACTIONABLE_SCHEDULE_STATES.has(booking?.state);
}

const serviceResourceTypes = (services = []) => {
  const doctorIds = new Set();
  const roomIds = new Set();

  services.forEach((service) => {
    [service?.resource, ...(service?.doctorOptions || [])]
      .filter(Boolean)
      .forEach((id) => doctorIds.add(id));
    [service?.room, ...(service?.roomOptions || [])]
      .filter(Boolean)
      .forEach((id) => roomIds.add(id));
  });

  return { doctorIds, roomIds };
};

export function bookingResourceIds(booking, services = []) {
  if (!booking) return [];
  const service = services.find((item) => item.id === booking.serviceId);
  const canonicalAssignments = [booking.doctorId, booking.roomId].filter(Boolean);

  // Typed assignment fields are the current snapshot. Legacy resource arrays
  // can contain a doctor's or room's previous ID after a reschedule, so they
  // are only used to fill a typed field that is genuinely unavailable.
  if (canonicalAssignments.length) {
    const { doctorIds, roomIds } = serviceResourceTypes(services);
    const legacyAssignments = [
      ...(booking.resourceIds || []),
      booking.resourceId,
    ].filter(Boolean);
    const missingDoctorId = booking.doctorId
      ? null
      : legacyAssignments.find((id) => doctorIds.has(id));
    const missingRoomId = booking.roomId
      ? null
      : legacyAssignments.find((id) => roomIds.has(id));

    return Array.from(new Set([
      booking.doctorId,
      booking.roomId,
      missingDoctorId,
      missingRoomId,
    ].filter(Boolean)));
  }

  const legacyAssignments = [
    ...(booking.resourceIds || []),
    booking.resourceId,
  ].filter(Boolean);
  if (legacyAssignments.length) return Array.from(new Set(legacyAssignments));

  return Array.from(new Set([
    service?.resource,
    service?.room,
  ].filter(Boolean)));
}

export function bookingAssignments(booking, services = [], resources = []) {
  const ids = bookingResourceIds(booking, services);
  const typed = ids.reduce((result, id) => {
    const resource = resources.find((item) => item.id === id);
    if (resource?.type === "person") result.doctorIds.push(id);
    if (resource?.type === "room") result.roomIds.push(id);
    return result;
  }, { doctorIds: [], roomIds: [] });

  return {
    ...typed,
    doctorId: booking?.doctorId || typed.doctorIds[0] || null,
    roomId: booking?.roomId || typed.roomIds[0] || null,
    allIds: ids,
  };
}

export function bookingWindow(booking) {
  const start = new Date(booking.start);
  const end = new Date(booking.end);
  const bufferMinutes = Number(booking.buffer) || 0;
  return {
    start,
    end,
    bufferedEnd: new Date(end.getTime() + bufferMinutes * 60 * 1000),
  };
}

const dayKey = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const clockMinutes = (value, fallback = 0) => {
  if (Number.isFinite(Number(value))) return Number(value);
  const [hours, minutes] = String(value || "").split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallback;
  return hours * 60 + minutes;
};

const atLocalMinute = (date, minute) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  result.setMinutes(minute);
  return result;
};

const dutyAppliesOn = (duty, date) => {
  if (!duty || Number.isNaN(date.getTime())) return false;
  if (duty.date && dayKey(duty.date) !== dayKey(date)) return false;
  if (!duty.date && Array.isArray(duty.weekdays) && !duty.weekdays.includes(date.getDay())) return false;
  if (duty.effectiveFrom && dayKey(date) < dayKey(duty.effectiveFrom)) return false;
  if (duty.effectiveTo && dayKey(date) > dayKey(duty.effectiveTo)) return false;
  return true;
};

const dutySupportsService = (duty, serviceId) => (
  !Array.isArray(duty?.serviceIds)
  || duty.serviceIds.length === 0
  || duty.serviceIds.includes(serviceId)
);

const serviceDoctorIds = (service = {}) => (
  service.doctorOptions?.length
    ? service.doctorOptions
    : [service.resource].filter(Boolean)
);

const serviceRoomIds = (service = {}) => (
  service.roomOptions?.length
    ? service.roomOptions
    : [service.room].filter(Boolean)
);

const normalizeDates = (dates) => {
  if (!dates) return [];
  if (Array.isArray(dates)) {
    return Array.from(new Map(dates
      .map((value) => new Date(value))
      .filter((date) => !Number.isNaN(date.getTime()))
      .map((date) => [dayKey(date), date])).values());
  }
  if (dates.start || dates.end) {
    const start = new Date(dates.start || dates.end);
    const end = new Date(dates.end || dates.start);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const result = [];
    const cursor = new Date(start);
    const last = end >= start ? end : start;
    while (cursor <= last && result.length < 370) {
      result.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return result;
  }
  const date = new Date(dates);
  return Number.isNaN(date.getTime()) ? [] : [date];
};

// Expand recurring duties into concrete blocks for one calendar day. Date
// objects are intentional: Calendar can position the result without reparsing.
export function roomDutyBlocks(duties = [], date = new Date(), roomId = null) {
  const target = new Date(date);
  if (Number.isNaN(target.getTime())) return [];
  const requestedRooms = Array.isArray(roomId) ? roomId : roomId ? [roomId] : null;

  return (duties || []).flatMap((duty) => {
    if (!dutyAppliesOn(duty, target)) return [];
    if (requestedRooms && !requestedRooms.includes(duty.roomId)) return [];
    const startMinute = clockMinutes(duty.startMinute ?? duty.start, 0);
    const endMinute = clockMinutes(duty.endMinute ?? duty.end, 24 * 60);
    const startDate = atLocalMinute(target, startMinute);
    const endDate = atLocalMinute(target, endMinute);
    if (endDate <= startDate) endDate.setDate(endDate.getDate() + 1);
    return [{
      ...duty,
      startMinute,
      endMinute,
      startDate,
      endDate,
      date: dayKey(target),
    }];
  }).sort((a, b) => a.startDate - b.startDate || a.endDate - b.endDate);
}

// Resolve the other side of a requested appointment from the duty roster.
// A selected doctor is authoritative (doctor-first intent); a selected room is
// authoritative (room/service-first intent). With neither selected, service
// compatibility determines both. The full appointment plus buffer must fit in
// one duty block, so a room hand-off never creates a phantom bookable slot.
const coveringDutyAssignments = ({
  duties = [],
  service,
  start,
  end,
  buffer,
  doctorId = null,
  roomId = null,
} = {}) => {
  if (!service?.id || !start || !end) return [];

  const startDate = new Date(start);
  const endDate = new Date(end);
  const bufferMinutes = Number(buffer ?? service.buffer) || 0;
  const bufferedEnd = new Date(endDate.getTime() + bufferMinutes * 60000);
  if ([startDate, endDate, bufferedEnd].some((date) => Number.isNaN(date.getTime()))) return [];
  if (endDate <= startDate) return [];

  const allowedDoctors = serviceDoctorIds(service);
  const allowedRooms = serviceRoomIds(service);

  const doctorPreference = new Map(allowedDoctors.map((id, index) => [id, index]));
  const roomPreference = new Map(allowedRooms.map((id, index) => [id, index]));

  return roomDutyBlocks(duties, startDate, roomId)
    .filter((duty) => !doctorId || duty.doctorId === doctorId)
    .filter((duty) => dutySupportsService(duty, service.id))
    // An explicit serviceId on an imported duty is authoritative capability
    // evidence even when the service's cached option lists lag behind.
    .filter((duty) => (
      duty.serviceIds?.includes(service.id)
      || !allowedDoctors.length
      || allowedDoctors.includes(duty.doctorId)
    ))
    .filter((duty) => (
      duty.serviceIds?.includes(service.id)
      || !allowedRooms.length
      || allowedRooms.includes(duty.roomId)
    ))
    .filter((duty) => startDate >= duty.startDate && bufferedEnd <= duty.endDate)
    .sort((left, right) => {
      const leftDoctor = left.doctorId === service.resource
        ? -1
        : (doctorPreference.get(left.doctorId) ?? Number.MAX_SAFE_INTEGER);
      const rightDoctor = right.doctorId === service.resource
        ? -1
        : (doctorPreference.get(right.doctorId) ?? Number.MAX_SAFE_INTEGER);
      const leftRoom = left.roomId === service.room
        ? -1
        : (roomPreference.get(left.roomId) ?? Number.MAX_SAFE_INTEGER);
      const rightRoom = right.roomId === service.room
        ? -1
        : (roomPreference.get(right.roomId) ?? Number.MAX_SAFE_INTEGER);
      return leftDoctor - rightDoctor
        || leftRoom - rightRoom
        || left.startDate - right.startDate
        || left.endDate - right.endDate;
    });
};

export function resolveDutyAssignment(options = {}) {
  const duty = coveringDutyAssignments(options)[0];
  if (!duty) return null;
  return {
    duty,
    doctorId: duty.doctorId,
    roomId: duty.roomId,
  };
}

// Returns the first duty reason that prevents an otherwise conflict-free
// booking. A full match must cover appointment end + buffer, not merely start.
export function findDutyAssignmentIssue(duties = [], candidate = {}) {
  const doctorId = candidate.doctorId || null;
  const roomId = candidate.roomId || null;
  if (!doctorId || !roomId) {
    return { type: "missing_assignment", doctorId, roomId };
  }
  if (!candidate.start || !candidate.end) {
    return { type: "invalid_time", doctorId, roomId };
  }

  const window = bookingWindow(candidate);
  if ([window.start, window.end, window.bufferedEnd].some((date) => Number.isNaN(date.getTime()))) {
    return { type: "invalid_time", doctorId, roomId };
  }

  const dayDuties = roomDutyBlocks(duties, window.start);
  const doctorRoomDuties = dayDuties
    .filter((duty) => duty.doctorId === doctorId && duty.roomId === roomId);
  const serviceDuties = doctorRoomDuties
    .filter((duty) => dutySupportsService(duty, candidate.serviceId));
  const coveringDuty = dayDuties.find((duty) => (
    duty.doctorId === doctorId
    && dutySupportsService(duty, candidate.serviceId)
    && window.start >= duty.startDate
    && window.bufferedEnd <= duty.endDate
  ));
  const exactDuty = serviceDuties.find((duty) => (
    window.start >= duty.startDate && window.bufferedEnd <= duty.endDate
  ));
  if (exactDuty) return null;

  if (!doctorRoomDuties.length) {
    return { type: "not_on_duty", doctorId, roomId, coveringDuty };
  }
  if (!serviceDuties.length) {
    return {
      type: "service_not_supported",
      doctorId,
      roomId,
      duty: doctorRoomDuties[0],
      coveringDuty,
    };
  }

  const overlappingDuty = serviceDuties.find((duty) => (
    window.start < duty.endDate && duty.startDate < window.bufferedEnd
  ));
  return {
    type: "outside_duty_window",
    doctorId,
    roomId,
    duty: overlappingDuty || serviceDuties[0],
    coveringDuty,
  };
}

export function dutyIssueMessage(issue, resources = [], language = getRuntimeLanguage()) {
  if (!issue) return "";
  const resourceById = new Map(resources.map((resource) => [resource.id, resource]));
  const t = (key, params = {}) => translateKey(key, params, language);
  const nameOf = (id, fallbackKey) => {
    const source = resourceById.get(id)?.short || resourceById.get(id)?.name;
    return source ? localizeCatalogSource(source, {}, language) : t(fallbackKey);
  };
  const join = (values) => values.join(language === "ka" ? " და " : " and ");
  const doctor = nameOf(issue.doctorId, "schedule.resource.selectedDoctor");
  const room = nameOf(issue.roomId, "schedule.resource.selectedRoom");
  const alternateRoom = issue.coveringDuty?.roomId
    ? nameOf(issue.coveringDuty.roomId, "schedule.resource.anotherRoom")
    : null;
  const time = (value) => value instanceof Date && !Number.isNaN(value.getTime())
    ? formatDateValue(value, language, { hour: "2-digit", minute: "2-digit" })
    : null;

  if (issue.type === "doctor_overlap" || issue.type === "room_overlap") {
    const date = issue.startDate instanceof Date && !Number.isNaN(issue.startDate.getTime())
      ? formatDateValue(issue.startDate, language, { weekday: "short", month: "short", day: "numeric" })
      : issue.date || t("schedule.date.thisDate");
    const start = time(issue.startDate);
    const end = time(issue.endDate);
    const range = start && end ? t("schedule.issue.timeRange", { start, end }) : "";
    if (issue.type === "doctor_overlap") {
      const rooms = Array.from(new Set((issue.duties || []).map((duty) => nameOf(duty.roomId, "schedule.resource.someRoom"))));
      return t("schedule.issue.doctorOverlap", {
        doctor: nameOf(issue.doctorId, "schedule.resource.doctor"),
        rooms: join(rooms),
        date,
        range,
      });
    }
    const doctors = Array.from(new Set((issue.duties || []).map((duty) => nameOf(duty.doctorId, "schedule.resource.someDoctor"))));
    return t("schedule.issue.roomOverlap", {
      room: nameOf(issue.roomId, "schedule.resource.room"),
      doctors: join(doctors),
      date,
      range,
    });
  }

  if (issue.type === "missing_assignment") return t("schedule.issue.missingAssignment");
  if (issue.type === "invalid_time") return t("schedule.issue.invalidTime");
  if (issue.type === "service_not_supported") {
    return t("schedule.issue.serviceUnsupported", { doctor, room });
  }
  if (issue.type === "outside_duty_window") {
    const start = time(issue.duty?.startDate);
    const end = time(issue.duty?.endDate);
    return start && end
      ? t("schedule.issue.outsideDutyWithRange", { doctor, room, start, end })
      : t("schedule.issue.outsideDuty", { doctor, room });
  }
  if (alternateRoom && issue.coveringDuty.roomId !== issue.roomId) {
    return t("schedule.issue.alternateRoom", { doctor, alternateRoom, room });
  }
  return t("schedule.issue.notOnDuty", { doctor, room });
}

const dutyRootId = (duty = {}) => {
  if (duty.id) return duty.id;
  const scheduleScope = duty.weekdays?.length
    ? `weekdays:${duty.weekdays.join(",")}`
    : `date:${duty.date || "unspecified"}`;
  return [
    duty.doctorId || "doctor",
    duty.roomId || "room",
    duty.startMinute ?? duty.start ?? "start",
    duty.endMinute ?? duty.end ?? "end",
    scheduleScope,
  ].join("|");
};

export function groupDutyRosterConflicts(conflicts = []) {
  const grouped = new Map();

  conflicts.forEach((conflict) => {
    const rootDutyIds = (conflict.duties || [])
      .map(dutyRootId)
      .sort();
    const key = `${conflict.type}:${rootDutyIds.join("::")}`;
    const occurrence = {
      date: conflict.date,
      startDate: conflict.startDate,
      endDate: conflict.endDate,
    };
    const current = grouped.get(key);

    if (!current) {
      grouped.set(key, {
        ...conflict,
        dutyIds: rootDutyIds,
        dates: conflict.date ? [conflict.date] : [],
        occurrences: [occurrence],
        occurrenceCount: 1,
      });
      return;
    }

    const occurrenceKey = `${occurrence.date || "date"}:${occurrence.startDate?.getTime?.()}:${occurrence.endDate?.getTime?.()}`;
    const alreadyIncluded = current.occurrences.some((item) => (
      `${item.date || "date"}:${item.startDate?.getTime?.()}:${item.endDate?.getTime?.()}` === occurrenceKey
    ));
    if (alreadyIncluded) return;

    current.occurrences.push(occurrence);
    if (occurrence.date && !current.dates.includes(occurrence.date)) current.dates.push(occurrence.date);
    current.occurrenceCount = current.occurrences.length;
  });

  return Array.from(grouped.values());
}

// Detect invalid imported/admin duty rosters before they create false
// availability. Recurring overlaps are one operational issue per root pair.
export function findDutyRosterConflicts(duties = [], dates = []) {
  const blocks = normalizeDates(dates).flatMap((date) => roomDutyBlocks(duties, date));
  const conflicts = [];

  for (let leftIndex = 0; leftIndex < blocks.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < blocks.length; rightIndex += 1) {
      const left = blocks[leftIndex];
      const right = blocks[rightIndex];
      if (left.date !== right.date || dutyRootId(left) === dutyRootId(right)) continue;
      const startDate = new Date(Math.max(left.startDate.getTime(), right.startDate.getTime()));
      const endDate = new Date(Math.min(left.endDate.getTime(), right.endDate.getTime()));
      if (startDate >= endDate) continue;

      const sameDoctor = left.doctorId && left.doctorId === right.doctorId;
      const sameRoom = left.roomId && left.roomId === right.roomId;

      // Multiple imported rows can intentionally describe the same staffed
      // room shift for different services. That is not an operational clash.
      if (sameDoctor && sameRoom) continue;

      if (sameDoctor) {
        conflicts.push({
          type: "doctor_overlap",
          doctorId: left.doctorId,
          date: left.date,
          startDate,
          endDate,
          dutyIds: [left.id, right.id],
          duties: [left, right],
        });
      } else if (sameRoom) {
        conflicts.push({
          type: "room_overlap",
          roomId: left.roomId,
          date: left.date,
          startDate,
          endDate,
          dutyIds: [left.id, right.id],
          duties: [left, right],
        });
      }
    }
  }
  return groupDutyRosterConflicts(conflicts);
}

// Unique staffed minutes per room; overlapping bad roster blocks are merged so
// capacity is not accidentally inflated while the separate health check warns.
export function dutyMinutesForRooms(duties = [], dates = [], roomIds = []) {
  const requested = roomIds?.length
    ? roomIds
    : Array.from(new Set((duties || []).map((duty) => duty.roomId).filter(Boolean)));
  const result = Object.fromEntries(requested.map((roomId) => [roomId, 0]));

  normalizeDates(dates).forEach((date) => {
    requested.forEach((roomId) => {
      const ranges = roomDutyBlocks(duties, date, roomId)
        .map((duty) => [duty.startDate.getTime(), duty.endDate.getTime()])
        .sort((a, b) => a[0] - b[0]);
      const merged = [];
      ranges.forEach(([start, end]) => {
        const tail = merged[merged.length - 1];
        if (!tail || start > tail[1]) merged.push([start, end]);
        else tail[1] = Math.max(tail[1], end);
      });
      result[roomId] += merged.reduce((sum, [start, end]) => sum + (end - start) / 60000, 0);
    });
  });
  return result;
}

export function findBookingConflictDetails(bookings, services, candidate, excludeId = null) {
  if (!isActionableScheduleBooking(candidate) || !candidate?.start || !candidate?.end) return [];
  const candidateResources = bookingResourceIds(candidate, services);
  const candidateWindow = bookingWindow(candidate);

  return bookings.flatMap((booking) => {
    if (booking.id === excludeId || !isActionableScheduleBooking(booking)) return [];
    const sharedResourceIds = bookingResourceIds(booking, services)
      .filter((resourceId) => candidateResources.includes(resourceId));
    if (!sharedResourceIds.length) return [];

    const window = bookingWindow(booking);
    const overlaps = candidateWindow.start < window.bufferedEnd
      && window.start < candidateWindow.bufferedEnd;
    if (!overlaps) return [];

    const appointmentOverlap = candidateWindow.start < window.end
      && window.start < candidateWindow.end;
    return [{
      booking,
      sharedResourceIds,
      type: appointmentOverlap ? "occupied" : "buffer",
    }];
  });
}

export function findBookingConflicts(bookings, services, candidate, excludeId = null) {
  return findBookingConflictDetails(bookings, services, candidate, excludeId)
    .map((detail) => detail.booking);
}

// Returns bookable choices from the same rules used by Calendar. Choices are
// deliberately spread across days so the customer gets a meaningful decision,
// not three adjacent times that only look different.
export function findAvailableSlots({
  bookings = [],
  services = [],
  serviceId,
  from = new Date(),
  count = 3,
  days = 14,
  startHour = 10,
  endHour = 19,
  stepMinutes = 30,
  excludeId = null,
  doctorId = null,
  roomId = null,
  duties = ROOM_DUTIES,
} = {}) {
  const service = services.find((item) => item.id === serviceId);
  if (!service || !service.duration) return [];

  const now = new Date(from);
  const duration = Number(service.duration);
  const buffer = Number(service.buffer) || 0;
  const slots = [];

  for (let dayOffset = 0; dayOffset < days && slots.length < count; dayOffset += 1) {
    const day = new Date(now);
    day.setHours(0, 0, 0, 0);
    day.setDate(day.getDate() + dayOffset);
    if (day.getDay() === 0) continue;

    const isToday = day.toDateString() === now.toDateString();
    const roundedNow = Math.ceil((now.getHours() * 60 + now.getMinutes()) / stepMinutes) * stepMinutes;
    const firstMinute = Math.max(startHour * 60, isToday ? roundedNow : startHour * 60);
    let choicesThisDay = 0;
    const hasDutyRoster = Array.isArray(duties) && duties.length > 0;

    for (let minute = firstMinute; minute < endHour * 60 && slots.length < count; minute += stepMinutes) {
      const start = new Date(day);
      start.setMinutes(minute);
      const end = new Date(start.getTime() + duration * 60000);
      const bufferedEnd = new Date(end.getTime() + buffer * 60000);
      const dayEnd = atLocalMinute(day, endHour * 60);
      if (bufferedEnd > dayEnd) break;

      const assignmentChoices = hasDutyRoster
        ? coveringDutyAssignments({
            duties,
            service,
            start,
            end,
            buffer,
            doctorId,
            roomId,
          })
        : [{ doctorId: doctorId || service.resource, roomId: roomId || service.room }];
      let available = null;
      for (const duty of assignmentChoices) {
        const assignedDoctorId = duty.doctorId || doctorId || service.resource;
        const assignedRoomId = duty.roomId || roomId || service.room;
        const candidate = {
          id: `slot-${start.getTime()}-${assignedDoctorId || "doctor"}-${assignedRoomId || "room"}`,
          serviceId: service.id,
          resourceId: assignedDoctorId,
          doctorId: assignedDoctorId,
          roomId: assignedRoomId,
          resourceIds: [assignedDoctorId, assignedRoomId].filter(Boolean),
          start: start.toISOString(),
          end: end.toISOString(),
          buffer,
          state: "pending",
          ...(duty.id ? { dutyId: duty.id } : {}),
        };
        if (findBookingConflicts(bookings, services, candidate, excludeId).length) continue;
        available = candidate;
        break;
      }
      if (!available) continue;

      slots.push(available);
      choicesThisDay += 1;
      if (choicesThisDay >= 2) break;
    }
  }

  return slots;
}

export function bookingDurationMinutes(booking) {
  if (!booking?.start || !booking?.end) return 0;
  return Math.max(0, (new Date(booking.end) - new Date(booking.start)) / 60000);
}

export function activeBooking(booking) {
  return booking?.state !== "cancelled";
}

// One attendance signal for every booking surface. Outcomes are observed;
// future attendance remains an estimate and is never presented as certainty.
export function getAttendanceSignal(booking) {
  if (!booking) return null;
  if (booking.state === "completed") {
    return { label: "Showed up", shortLabel: "Showed up", source: "observed", tone: "success" };
  }
  if (booking.state === "noshow") {
    return { label: "Did not show", shortLabel: "No-show", source: "observed", tone: "destructive" };
  }
  if (booking.state === "cancelled") {
    return { label: "Cancelled", shortLabel: "Cancelled", source: "observed", tone: "muted" };
  }

  const explicit = Number(booking.showUpProbability);
  const fallback = booking.source === "ilia" && Number.isFinite(Number(booking.risk))
    ? 100 - Number(booking.risk)
    : 84;
  const probability = Math.max(1, Math.min(99, Number.isFinite(explicit) ? explicit : fallback));
  return {
    probability,
    label: `${probability}% likely to show`,
    shortLabel: `${probability}% show`,
    source: booking.showUpSource || "prediction",
    tone: probability < 60 ? "destructive" : probability < 75 ? "warning" : "success",
  };
}
